import {STATUS_TYPES} from '../../render/constants'
import type Status from '../../render/status'
import {isSvgRequest, getSvgUrl, loadImage, loadSvg} from './utils'

/**
 * This function is somewhat complex because we want to try and support
 * svgs that should be valid but are malformed due to a missing xmlns namespace
 * attribute.
 *
 * Previously, we parsed this using 'text/xml' but this depends on a valid namespace
 * existing. So, we instead parse the string as an html tag, and attempt to find the
 * svg element.
 *
 * Throwing an error here feels best, since it indicates we recieved a response from
 * the server that was expected to be an SVG, but wasn't.
 */
function parseSVG(data: string) {
  const parser = new DOMParser()
  const svg = parser.parseFromString(data, 'text/html')
  const svgElement = svg?.children?.[0]?.querySelector('svg')

  if (svgElement?.nodeName !== 'svg') {
    throw new Error('Invalid element, not an SVG node.')
  }
  return svgElement
}

class ImageLoader {
  constructor(protected status: Status) {
    this.status = status
  }

  handleError(error: Error) {
    window.debug('Error loading image: ', error.message)
    this.status.set(STATUS_TYPES.fatal, {error: 'Invalid image source.'})
  }

  handleSuccess = (url: string, altText: string, data: string, xhr: Response): Promise<HTMLImageElement> => {
    const didLoadSvg = isSvgRequest(xhr)

    if (!didLoadSvg) {
      return loadImage(url, altText)
    }

    const svgElement = parseSVG(data)

    return loadSvg(getSvgUrl(svgElement, url), altText, svgElement)
  }

  fetch = (
    url: string,
    altText: string,
    onSuccess?: (image: HTMLImageElement) => void,
    onError?: (error: Error) => void,
  ) => {
    window.debug(`Attempting to load image URL: [${url}]`)
    this.status.load(url, {
      json: false,
      success: async (document, xhr: Response) => {
        try {
          const image = await this.handleSuccess(url, altText, document as string, xhr)
          onSuccess?.(image)
        } catch (error: unknown) {
          onError?.(error as Error)
          this.handleError(error as Error)
        }
      },
      error: (error: Error) => {
        onError?.(error)
        this.handleError(error)
      },
    })
  }
}

export {ImageLoader}
